import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import api from '../../lib/api'
import {
  Building,
  Search,
  MoreHorizontal,
  Eye,
  Ban,
  CheckCircle,
  Trash2,
  Filter,
  Download,
  User,
  UserX,
  ClipboardCheck,
  PhoneIncoming,
  PhoneOutgoing,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  ArrowUpRight,
  ArrowDownRight,
  ArrowRight
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { PageHeader } from '@/components/dashboard-ui'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { useToast } from '@/hooks/use-toast'
import { TierBadge, formatMonthly } from '@/components/ui/TierBadge'

interface Organization {
  id: string
  slug: string
  name: string
  status: 'pending' | 'active' | 'suspended' | 'cancelled'
  subscriptionTier: string
  subscriptionStatus: string
  trialEndsAt: string | null
  isComped?: boolean
  customMonthlyCents?: number | null
  monthlyAmountCents?: number
  subscriptionTierPrevious?: string | null
  subscriptionTierChangedAt?: string | null
  agentCount: number
  conversationCount: number
  voiceCostThisMonth: number
  phoneMonthlyCost: number
  ownerEmail: string | null
  createdAt: string
  updatedAt: string
}

// Tier rank for comparing up/down direction on a change.
const TIER_RANK: Record<string, number> = {
  trial: 0, associate: 1, partner: 2, principal: 3, enterprise: 4,
}

export default function OrganizationsPage() {
  const [organizations, setOrganizations] = useState<Organization[]>([])
  const [total, setTotal] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [tierFilter, setTierFilter] = useState('all')
  const [offset, setOffset] = useState(0)
  const [sortBy, setSortBy] = useState('created_at')
  const limit = 20
  const [showStatusDialog, setShowStatusDialog] = useState(false)
  const [showDetailsDialog, setShowDetailsDialog] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null)
  const [newStatus, setNewStatus] = useState<string>('')
  const [statusReason, setStatusReason] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [deleteConfirmText, setDeleteConfirmText] = useState('')
  const [orgDetails, setOrgDetails] = useState<any>(null)
  const [deletingAgentId, setDeletingAgentId] = useState<string | null>(null)
  const [showVerifyDialog, setShowVerifyDialog] = useState(false)
  const [verifyResults, setVerifyResults] = useState<any>(null)
  const [isVerifying, setIsVerifying] = useState(false)
  const { toast } = useToast()

  useEffect(() => {
    loadOrganizations()
  }, [statusFilter, tierFilter, offset, sortBy])

  const loadOrganizations = async () => {
    try {
      const params = new URLSearchParams()
      params.append('limit', limit.toString())
      params.append('offset', offset.toString())
      params.append('sortBy', sortBy)
      params.append('sortOrder', sortBy === 'name' ? 'asc' : 'desc')
      if (statusFilter !== 'all') params.append('status', statusFilter)
      if (tierFilter !== 'all') params.append('subscriptionTier', tierFilter)
      if (searchQuery) params.append('search', searchQuery)

      const response = await api.get(`/api/platform/organizations?${params.toString()}`)
      if (response.data.success) {
        setOrganizations(response.data.organizations)
        setTotal(response.data.total)
      }
    } catch (error) {
      console.error('Failed to load organizations:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleSearch = () => {
    setOffset(0)
    loadOrganizations()
  }

  const handleViewDetails = async (org: Organization) => {
    setSelectedOrg(org)
    setOrgDetails(null) // Clear stale data from previous org before loading
    setShowDetailsDialog(true)

    try {
      const response = await api.get(`/api/platform/organizations/${org.id}`)
      if (response.data.success) {
        setOrgDetails(response.data.organization)
      }
    } catch (error) {
      console.error('Failed to load organization details:', error)
    }
  }

  const handleUpdateStatus = async () => {
    if (!selectedOrg || !newStatus) return

    setIsSubmitting(true)
    try {
      await api.put(`/api/platform/organizations/${selectedOrg.id}/status`, {
        status: newStatus,
        reason: statusReason
      })

      toast({
        title: 'Status updated',
        description: `Organization status changed to ${newStatus}`
      })

      setShowStatusDialog(false)
      setSelectedOrg(null)
      setNewStatus('')
      setStatusReason('')
      loadOrganizations()
    } catch (error: any) {
      toast({
        title: 'Update failed',
        description: error.response?.data?.message || 'Failed to update status',
        variant: 'destructive'
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDeleteOrganization = async () => {
    if (!selectedOrg || deleteConfirmText !== selectedOrg.name) return

    setIsDeleting(true)
    try {
      const response = await api.delete(`/api/platform/organizations/${selectedOrg.id}`)

      toast({
        title: 'Organization deleted',
        description: response.data.message || `${selectedOrg.name} has been permanently deleted`
      })

      setShowDeleteDialog(false)
      setSelectedOrg(null)
      setDeleteConfirmText('')
      loadOrganizations()
    } catch (error: any) {
      toast({
        title: 'Delete failed',
        description: error.response?.data?.message || 'Failed to delete organization',
        variant: 'destructive'
      })
    } finally {
      setIsDeleting(false)
    }
  }

  const handleDeleteAgent = async (agentId: string, agentEmail: string) => {
    if (!confirm(`Are you sure you want to delete user "${agentEmail}"? This action cannot be undone.`)) {
      return
    }

    setDeletingAgentId(agentId)
    try {
      const response = await api.delete(`/api/platform/agents/${agentId}`)

      toast({
        title: 'User deleted',
        description: response.data.message || `${agentEmail} has been deleted`
      })

      // Refresh organization details to update the users list
      if (selectedOrg) {
        const detailsResponse = await api.get(`/api/platform/organizations/${selectedOrg.id}`)
        if (detailsResponse.data.success) {
          setOrgDetails(detailsResponse.data.organization)
        }
      }
    } catch (error: any) {
      toast({
        title: 'Delete failed',
        description: error.response?.data?.message || 'Failed to delete user',
        variant: 'destructive'
      })
    } finally {
      setDeletingAgentId(null)
    }
  }

  const handleVerifyOnboarding = async (org: Organization) => {
    setSelectedOrg(org)
    setShowVerifyDialog(true)
    setIsVerifying(true)
    setVerifyResults(null)

    try {
      const response = await api.get(`/api/admin/verify-onboarding/${org.id}`)
      if (response.data.success) {
        setVerifyResults(response.data)
      }
    } catch (error: any) {
      toast({
        title: 'Verification failed',
        description: error.response?.data?.message || 'Failed to verify onboarding',
        variant: 'destructive'
      })
      setShowVerifyDialog(false)
    } finally {
      setIsVerifying(false)
    }
  }

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
      active: 'default',
      pending: 'secondary',
      suspended: 'destructive',
      cancelled: 'outline'
    }
    return <Badge variant={variants[status] || 'outline'}>{status}</Badge>
  }

  const getTierBadge = (tier: string) => <TierBadge tier={tier} />

  // Relative-time badge for orgs whose tier changed in the last 30 days —
  // gives platform admin at-a-glance visibility into who just up- or
  // downgraded so manual reconciliation (e.g., phone number deprovisioning
  // after a drop) can be scheduled without polling an audit table.
  const getRecentChangeCell = (org: Organization) => {
    if (!org.subscriptionTierPrevious || !org.subscriptionTierChangedAt) {
      return <span className="text-xs text-muted-foreground">—</span>
    }
    const changedAt = new Date(org.subscriptionTierChangedAt)
    const daysAgo = (Date.now() - changedAt.getTime()) / (1000 * 60 * 60 * 24)
    if (daysAgo > 30) return <span className="text-xs text-muted-foreground">—</span>

    const prevRank = TIER_RANK[org.subscriptionTierPrevious] ?? -1
    const currRank = TIER_RANK[org.subscriptionTier] ?? -1
    const direction: 'up' | 'down' | 'same' =
      currRank > prevRank ? 'up' : currRank < prevRank ? 'down' : 'same'

    const ArrowIcon = direction === 'up' ? ArrowUpRight : direction === 'down' ? ArrowDownRight : ArrowRight
    const color =
      direction === 'up'
        ? 'text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 border-emerald-500/30'
        : direction === 'down'
          ? 'text-amber-600 dark:text-amber-400 bg-amber-500/10 border-amber-500/30'
          : 'text-muted-foreground bg-muted border-border'

    const timeLabel =
      daysAgo < 1
        ? `${Math.max(1, Math.round(daysAgo * 24))}h ago`
        : `${Math.round(daysAgo)}d ago`

    return (
      <div className="flex flex-col gap-0.5">
        <span className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] font-medium w-fit ${color}`}>
          <ArrowIcon className="h-3 w-3" />
          <span className="capitalize">{org.subscriptionTierPrevious}</span>
          <span>→</span>
          <span className="capitalize">{org.subscriptionTier}</span>
        </span>
        <span className="text-[10px] text-muted-foreground ml-1">{timeLabel}</span>
      </div>
    )
  }

  // Monthly revenue cell: $0 comped (violet tag), "— custom" hint when negotiated,
  // plain amount otherwise. Shows the amount we actually bill them, not our cost.
  const getMonthlyCell = (org: Organization) => {
    if (org.isComped) {
      return (
        <span className="inline-flex items-center gap-1.5 text-sm">
          <span className="font-mono">$0</span>
          <Badge variant="outline" className="text-[9px] px-1.5 py-0 h-4 bg-violet-500/10 text-violet-600 dark:text-violet-400 border-violet-500/30">COMPED</Badge>
        </span>
      )
    }
    if (org.customMonthlyCents !== null && org.customMonthlyCents !== undefined) {
      return (
        <span className="inline-flex items-center gap-1.5 text-sm">
          <span className="font-mono font-medium">{formatMonthly(org.monthlyAmountCents ?? org.customMonthlyCents)}</span>
          <Badge variant="outline" className="text-[9px] px-1.5 py-0 h-4">CUSTOM</Badge>
        </span>
      )
    }
    return <span className="font-mono text-sm">{formatMonthly(org.monthlyAmountCents)}</span>
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-64 bg-muted animate-pulse rounded" />
        <div className="h-96 bg-muted animate-pulse rounded-lg" />
      </div>
    )
  }

  const exportCsv = () => {
    if (organizations.length === 0) return
    const headers = ['Name', 'Slug', 'Status', 'Tier', 'Owner', 'Agents', 'Conversations', 'Voice Cost', 'Created']
    const rows = organizations.map(o => [
      o.name, o.slug, o.status, o.subscriptionTier, o.ownerEmail || '',
      o.agentCount, o.conversationCount, `$${(o.voiceCostThisMonth || 0).toFixed(2)}`,
      new Date(o.createdAt).toLocaleDateString()
    ])
    const csv = [headers, ...rows].map(r => r.map(v => `"${v}"`).join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `organizations-${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="space-y-5 sm:space-y-6">
      <PageHeader
        eyebrow="The Castle"
        title="Organizations"
        subtitle="Manage all organizations on the platform."
        actions={
          <Button variant="outline" onClick={exportCsv}>
            <Download className="mr-2 h-4 w-4" />
            Export
          </Button>
        }
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="card-inset">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{total}</div>
          </CardContent>
        </Card>
        <Card className="card-inset">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Active</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {organizations.filter((o) => o.status === 'active').length}
            </div>
          </CardContent>
        </Card>
        <Card className="card-inset">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Pending</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">
              {organizations.filter((o) => o.status === 'pending').length}
            </div>
          </CardContent>
        </Card>
        <Card className="card-inset">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Suspended</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {organizations.filter((o) => o.status === 'suspended').length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="card-inset">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or slug..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]">
                <Filter className="mr-2 h-4 w-4" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="suspended">Suspended</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
            <Select value={tierFilter} onValueChange={setTierFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Tier" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Tiers</SelectItem>
                <SelectItem value="trial">Trial</SelectItem>
                <SelectItem value="associate">Associate</SelectItem>
                <SelectItem value="partner">Partner</SelectItem>
                <SelectItem value="principal">Principal</SelectItem>
                <SelectItem value="enterprise">Enterprise</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sortBy} onValueChange={(v) => { setSortBy(v); setOffset(0) }}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="created_at">Newest First</SelectItem>
                <SelectItem value="name">A → Z</SelectItem>
                <SelectItem value="status">Status</SelectItem>
                <SelectItem value="subscription_tier">Tier</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={handleSearch}>Search</Button>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="card-inset">
        <CardContent className="p-0">
          <p className="text-[11px] text-muted-foreground px-4 pt-3 sm:hidden">
            Swipe sideways to see more columns →
          </p>
          <div className="overflow-x-auto">
          <Table className="min-w-[1100px]">
            <TableHeader>
              <TableRow>
                <TableHead className="sticky left-0 z-10 bg-card">Organization</TableHead>
                <TableHead className="hidden md:table-cell">Owner Email</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Tier</TableHead>
                <TableHead className="hidden lg:table-cell">Billed/Mo</TableHead>
                <TableHead className="hidden xl:table-cell">Recent change</TableHead>
                <TableHead className="hidden lg:table-cell">Agents</TableHead>
                <TableHead className="hidden xl:table-cell">Conversations</TableHead>
                <TableHead className="hidden xl:table-cell">Est. Cost/Mo</TableHead>
                <TableHead className="hidden lg:table-cell">Created</TableHead>
                <TableHead className="w-[80px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {organizations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} className="text-center py-8 text-muted-foreground">
                    No organizations found
                  </TableCell>
                </TableRow>
              ) : (
                organizations.map((org) => (
                  <TableRow key={org.id}>
                    <TableCell className="sticky left-0 z-10 bg-card">
                      <Link to={`/admin/organizations/${org.id}`} className="flex items-center gap-3 hover:underline">
                        <div className="h-8 w-8 rounded bg-muted flex items-center justify-center">
                          <Building className="h-4 w-4" />
                        </div>
                        <div>
                          <p className="font-medium">{org.name}</p>
                          <p className="text-xs text-muted-foreground">{org.slug}</p>
                        </div>
                      </Link>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <span className="text-sm text-muted-foreground">{org.ownerEmail || '—'}</span>
                    </TableCell>
                    <TableCell>{getStatusBadge(org.status)}</TableCell>
                    <TableCell>{getTierBadge(org.subscriptionTier)}</TableCell>
                    <TableCell className="hidden lg:table-cell">{getMonthlyCell(org)}</TableCell>
                    <TableCell className="hidden xl:table-cell">{getRecentChangeCell(org)}</TableCell>
                    <TableCell className="hidden lg:table-cell">{org.agentCount}</TableCell>
                    <TableCell className="hidden xl:table-cell">{org.conversationCount.toLocaleString()}</TableCell>
                    <TableCell className="hidden xl:table-cell font-mono text-sm">
                      {(org.voiceCostThisMonth + org.phoneMonthlyCost) > 0
                        ? `$${(org.voiceCostThisMonth + org.phoneMonthlyCost).toFixed(2)}`
                        : '-'}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">{new Date(org.createdAt).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleViewDetails(org)}>
                            <Eye className="mr-2 h-4 w-4" />
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleVerifyOnboarding(org)}>
                            <ClipboardCheck className="mr-2 h-4 w-4" />
                            Verify Onboarding
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {org.status !== 'active' && (
                            <DropdownMenuItem
                              onClick={() => {
                                setSelectedOrg(org)
                                setNewStatus('active')
                                setShowStatusDialog(true)
                              }}
                            >
                              <CheckCircle className="mr-2 h-4 w-4" />
                              Activate
                            </DropdownMenuItem>
                          )}
                          {org.status !== 'suspended' && (
                            <DropdownMenuItem
                              onClick={() => {
                                setSelectedOrg(org)
                                setNewStatus('suspended')
                                setShowStatusDialog(true)
                              }}
                            >
                              <Ban className="mr-2 h-4 w-4" />
                              Suspend
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-destructive"
                            onClick={() => {
                              setSelectedOrg(org)
                              setShowDeleteDialog(true)
                            }}
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          </div>
        </CardContent>
      </Card>

      {/* Pagination */}
      {total > limit && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {offset + 1} to {Math.min(offset + limit, total)} of {total} organizations
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setOffset(Math.max(0, offset - limit))}
              disabled={offset === 0}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setOffset(offset + limit)}
              disabled={offset + limit >= total}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Status Update Dialog */}
      <Dialog open={showStatusDialog} onOpenChange={(open) => {
        setShowStatusDialog(open)
        if (!open) { setNewStatus(''); setStatusReason('') }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {newStatus === 'active' ? 'Activate' : newStatus === 'suspended' ? 'Suspend' : 'Update'} Organization
            </DialogTitle>
            <DialogDescription>
              {newStatus === 'suspended'
                ? `This will suspend ${selectedOrg?.name} and prevent access to their dashboard.`
                : `This will activate ${selectedOrg?.name} and restore access.`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="reason">Reason (optional)</Label>
              <Textarea
                id="reason"
                placeholder="Enter a reason for this action..."
                value={statusReason}
                onChange={(e) => setStatusReason(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStatusDialog(false)}>
              Cancel
            </Button>
            <Button
              variant={newStatus === 'suspended' ? 'destructive' : 'default'}
              onClick={handleUpdateStatus}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Updating...' : `${newStatus === 'active' ? 'Activate' : 'Suspend'}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Details Dialog */}
      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedOrg?.name}</DialogTitle>
            <DialogDescription>{selectedOrg?.slug}</DialogDescription>
          </DialogHeader>
          {orgDetails ? (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Status</Label>
                  <p className="font-medium">{getStatusBadge(orgDetails.status)}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Subscription</Label>
                  <p className="font-medium">{getTierBadge(orgDetails.subscriptionTier)}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Created</Label>
                  <p className="font-medium">{new Date(orgDetails.createdAt).toLocaleString()}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Trial Ends</Label>
                  <p className="font-medium">
                    {orgDetails.trialEndsAt
                      ? new Date(orgDetails.trialEndsAt).toLocaleDateString()
                      : 'N/A'}
                  </p>
                </div>
              </div>

              {orgDetails.stats && (
                <div>
                  <Label className="text-muted-foreground mb-2 block">Usage Stats</Label>
                  <div className="grid grid-cols-3 gap-4 bg-muted rounded-lg p-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold">{orgDetails.stats.agents}</p>
                      <p className="text-xs text-muted-foreground">Agents</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold">{orgDetails.stats.conversations?.total || 0}</p>
                      <p className="text-xs text-muted-foreground">Total Conversations</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold">{orgDetails.stats.calls?.totalMinutes || 0}</p>
                      <p className="text-xs text-muted-foreground">Voice Minutes</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Users List */}
              {orgDetails.agents && orgDetails.agents.length > 0 && (
                <div>
                  <Label className="text-muted-foreground mb-2 block">Users ({orgDetails.agents.length})</Label>
                  <div className="border rounded-lg divide-y max-h-64 overflow-y-auto">
                    {orgDetails.agents.map((agent: any) => (
                      <div key={agent.id} className="flex items-center justify-between p-3">
                        <div className="flex items-center gap-3">
                          <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center">
                            <User className="h-4 w-4" />
                          </div>
                          <div>
                            <p className="font-medium text-sm">{agent.name}</p>
                            <p className="text-xs text-muted-foreground">{agent.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">{agent.role}</Badge>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                            onClick={() => handleDeleteAgent(agent.id, agent.email)}
                            disabled={deletingAgentId === agent.id}
                          >
                            {deletingAgentId === agent.id ? (
                              <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                            ) : (
                              <UserX className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="py-8 text-center text-muted-foreground">
              Loading details...
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setShowDetailsDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={(open) => {
        setShowDeleteDialog(open)
        if (!open) {
          setDeleteConfirmText('')
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-destructive">Delete Organization</DialogTitle>
            <DialogDescription>
              This action cannot be undone. This will permanently delete{' '}
              <strong>{selectedOrg?.name}</strong> and all associated data including:
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
              <li>All users/agents in the organization</li>
              <li>All conversations and messages</li>
              <li>All voice calls and recordings</li>
              <li>Phone numbers and voice assistants</li>
              <li>Knowledge base and integrations</li>
              <li>Usage records and billing history</li>
            </ul>
            <div className="space-y-2">
              <Label htmlFor="confirm">
                Type <strong>{selectedOrg?.name}</strong> to confirm:
              </Label>
              <Input
                id="confirm"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder={selectedOrg?.name}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteOrganization}
              disabled={isDeleting || deleteConfirmText !== selectedOrg?.name}
            >
              {isDeleting ? 'Deleting...' : 'Delete Organization'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Onboarding Verification Dialog */}
      <Dialog open={showVerifyDialog} onOpenChange={setShowVerifyDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardCheck className="h-5 w-5" />
              Onboarding Verification
            </DialogTitle>
            <DialogDescription>
              Verifying voice setup for {selectedOrg?.name}
            </DialogDescription>
          </DialogHeader>

          {isVerifying ? (
            <div className="py-12 text-center">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto mb-4" />
              <p className="text-muted-foreground">Running verification checks...</p>
            </div>
          ) : verifyResults ? (
            <div className="space-y-6">
              {/* Readiness Status */}
              <div className="grid grid-cols-2 gap-4">
                <div className={`p-4 rounded-lg border ${verifyResults.readiness?.inboundCalls ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <PhoneIncoming className={`h-4 w-4 ${verifyResults.readiness?.inboundCalls ? 'text-green-600' : 'text-red-600'}`} />
                    <span className="font-medium">AI Receptionist</span>
                  </div>
                  <p className={`text-sm ${verifyResults.readiness?.inboundCalls ? 'text-green-700' : 'text-red-700'}`}>
                    {verifyResults.readiness?.inboundCalls ? 'Ready' : 'Not Ready'}
                  </p>
                </div>
                <div className={`p-4 rounded-lg border ${verifyResults.readiness?.outboundCalls ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <PhoneOutgoing className={`h-4 w-4 ${verifyResults.readiness?.outboundCalls ? 'text-green-600' : 'text-red-600'}`} />
                    <span className="font-medium">Lead Follow-Up</span>
                  </div>
                  <p className={`text-sm ${verifyResults.readiness?.outboundCalls ? 'text-green-700' : 'text-red-700'}`}>
                    {verifyResults.readiness?.outboundCalls ? 'Ready' : 'Not Ready'}
                  </p>
                </div>
              </div>

              {/* Summary */}
              <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                <div className="flex items-center gap-6">
                  <div className="flex items-center gap-2 text-green-600">
                    <CheckCircle2 className="h-4 w-4" />
                    <span className="font-medium">{verifyResults.summary?.pass || 0} Pass</span>
                  </div>
                  <div className="flex items-center gap-2 text-yellow-600">
                    <AlertTriangle className="h-4 w-4" />
                    <span className="font-medium">{verifyResults.summary?.warn || 0} Warn</span>
                  </div>
                  <div className="flex items-center gap-2 text-red-600">
                    <XCircle className="h-4 w-4" />
                    <span className="font-medium">{verifyResults.summary?.fail || 0} Fail</span>
                  </div>
                </div>
                <Badge variant={
                  verifyResults.summary?.overallStatus === 'complete' ? 'default' :
                  verifyResults.summary?.overallStatus === 'partial' ? 'secondary' : 'destructive'
                }>
                  {verifyResults.summary?.overallStatus || 'unknown'}
                </Badge>
              </div>

              {/* Detailed Results */}
              <div className="space-y-2">
                <Label className="text-muted-foreground">Verification Results</Label>
                <div className="border rounded-lg divide-y max-h-64 overflow-y-auto">
                  {verifyResults.results?.map((result: any, index: number) => (
                    <div key={index} className="p-3 flex items-start gap-3">
                      {result.status === 'pass' ? (
                        <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                      ) : result.status === 'warn' ? (
                        <AlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                      ) : (
                        <XCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm">{result.check}</p>
                        <p className="text-xs text-muted-foreground">{result.details}</p>
                        {result.fix && (
                          <p className="text-xs text-primary mt-1">Fix: {result.fix}</p>
                        )}
                        {result.status === 'fail' && !result.fix && (
                          <p className="text-xs text-red-500 mt-1">Check the org's AI Agents page and Settings to resolve.</p>
                        )}
                        {result.data && (
                          <pre className="mt-1 text-xs bg-muted p-2 rounded overflow-x-auto">
                            {JSON.stringify(result.data, null, 2)}
                          </pre>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : null}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => selectedOrg && handleVerifyOnboarding(selectedOrg)}
              disabled={isVerifying}
            >
              Re-run Verification
            </Button>
            <Button onClick={() => setShowVerifyDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
